import React, { useEffect, useRef, useCallback } from "react";
import { callAnalytics } from "../utils/amplitude";

const ScrollTracker = ({
  children,
  threshold = 0.8,
  eventProperties = {},
  once = true,
}) => {
  const containerRef = useRef(null);
  const hasTriggered = useRef(false);
  const scrollThresholds = useRef(new Set());

  const calculateScrollPercentage = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Calculate scroll percentage (0-100)
    const scrollPercentage =
      (scrollTop / (documentHeight - windowHeight)) * 100;

    return Math.round(scrollPercentage);
  }, []);

  const handleScroll = useCallback(() => {
    if (hasTriggered.current && once) return;

    const scrollPercentage = calculateScrollPercentage();

    // Check if we've crossed the threshold
    if (
      scrollPercentage >= threshold * 100 &&
      !scrollThresholds.current.has(Math.floor(scrollPercentage / 10))
    ) {

      const finalEventName = "page_scroll_event";
      const finalEventProperties = {
        ...eventProperties,
      };

      callAnalytics(finalEventName, finalEventProperties);

      // Track this threshold to avoid duplicate events
      scrollThresholds.current.add(Math.floor(scrollPercentage / 10));

      if (once) {
        hasTriggered.current = true;
      }
    }
  }, [threshold, eventProperties, once, calculateScrollPercentage]);

  useEffect(() => {
    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check in case page is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div ref={containerRef} data-testid="scroll-tracker">
      {children}
    </div>
  );
};

export default ScrollTracker;
